import React, { createContext, FC, useCallback, useMemo, useReducer } from "react";

import { noop } from "@/utils/functions";

import { WizardConfig } from "./WizardConfig";
import { wizardReducer } from "./wizardReducer";
import { WizardStep } from "./WizardStep";

export interface WizardContextValue {
  config: WizardConfig;
  steps: WizardStep[];
  currentStep: number;
  currentStepIndex: number;

  goToPrevStep: () => void;
  goToNextStep: () => void;
  goToStepByKey: (key: string) => void;
}

export const WizardContext = createContext<WizardContextValue>({
  config: {
    steps: [],
  },
  steps: [],
  currentStep: 0,
  currentStepIndex: 0,
  goToPrevStep: noop,
  goToNextStep: noop,
  goToStepByKey: noop,
});

export interface WizardProviderProps {
  config: WizardConfig;
}

export const WizardProvider: FC<WizardProviderProps> = ({ config, children }) => {
  const enabledStepsConfig = config.steps.filter(s => s.enabled === undefined || s.enabled === true);

  const initialStep = enabledStepsConfig.find(s => s.step === config.initialStep);

  const [state, dispatch] = useReducer(wizardReducer, {
    steps: enabledStepsConfig,
    currentStepIndex: initialStep ? enabledStepsConfig.indexOf(initialStep) : 0,
  });

  const goToPrevStep = useCallback(() => {
    dispatch({ type: "GO_TO_PREV_STEP" });
  }, []);

  const goToNextStep = useCallback(() => {
    dispatch({ type: "GO_TO_NEXT_STEP" });
  }, []);

  const goToStepByKey = useCallback((key: string) => {
    dispatch({ type: "GO_TO_STEP_BY_KEY", key });
  }, []);

  const contextValue = useMemo(
    () => ({
      ...state,

      config,
      currentStep: enabledStepsConfig[state.currentStepIndex].step,

      goToPrevStep,
      goToNextStep,
      goToStepByKey,
    }),
    [enabledStepsConfig, state, config, goToPrevStep, goToNextStep, goToStepByKey],
  );

  return <WizardContext.Provider value={contextValue}>{children}</WizardContext.Provider>;
};
